import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * 이용약관 페이지 (/terms)
 * 애드센스 승인 요건(필수 정책 페이지) 충족용.
 */

const SITE_NAME = 'CBT 편의점';
const SITE_DOMAIN = 'www.cbtstudy.com';
const SITE_CONTACT = 'support@cbtstudy.com';
const EFFECTIVE_DATE = '2026년 6월 14일';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>이용약관 - {SITE_NAME}</title>
        <meta
          name="description"
          content={`${SITE_NAME} 서비스 이용약관입니다. 서비스 이용 조건, 이용자의 권리와 의무, 면책 사항 등을 안내합니다.`}
        />
        <link rel="canonical" href={`https://${SITE_DOMAIN}/terms`} />
      </Helmet>

      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="text-lg font-bold text-gray-900">{SITE_NAME}</a>
          <a href="/" className="text-sm text-orange-500 hover:text-orange-600 transition-colors">← 홈으로</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">이용약관</h1>
        <p className="text-sm text-gray-500 mb-10">시행일: {EFFECTIVE_DATE}</p>

        <p className="text-gray-700 leading-relaxed mb-8">
          본 약관은 {SITE_NAME}(이하 "서비스")가 제공하는 국가자격증 기출문제 학습
          서비스의 이용과 관련하여 서비스와 이용자 간의 권리·의무 및 책임 사항을
          규정함을 목적으로 합니다.
        </p>

        <Section title="제1조 (목적 및 정의)">
          <p>
            "서비스"란 {SITE_NAME}가 웹사이트({SITE_DOMAIN})를 통해 제공하는 기출문제
            풀이, 해설, 학습 통계 등 일체의 서비스를 말합니다. "이용자"란 본 약관에
            따라 서비스를 이용하는 회원 및 비회원을 말합니다.
          </p>
        </Section>

        <Section title="제2조 (약관의 효력 및 변경)">
          <p>
            본 약관은 서비스 화면에 게시함으로써 효력이 발생합니다. 서비스는 관련 법령을
            위배하지 않는 범위에서 약관을 개정할 수 있으며, 변경 시 본 페이지를 통해
            공지합니다. 변경된 약관에 동의하지 않는 경우 이용자는 서비스 이용을 중단할 수
            있습니다.
          </p>
        </Section>

        <Section title="제3조 (서비스의 제공)">
          <ul className="list-disc pl-5 space-y-1">
            <li>서비스는 회차별 기출문제 및 해설, 모의고사·과목별 학습, 오답노트, 학습 통계 등을 제공합니다.</li>
            <li>서비스의 전부 또는 일부는 무료로 제공되며, 일부 기능은 로그인이 필요할 수 있습니다.</li>
            <li>서비스는 운영상·기술상 필요에 따라 제공 내용을 변경할 수 있습니다.</li>
          </ul>
        </Section>

        <Section title="제4조 (학습 콘텐츠와 정확성)">
          <p>
            서비스가 제공하는 기출문제와 해설은 학습 보조를 목적으로 합니다. 서비스는
            콘텐츠의 정확성을 위해 노력하지만, 출제 기관의 공식 자료와 차이가 있을 수
            있습니다. 시험의 최종 정답·기준은 해당 시험 시행 기관의 공식 발표를 따르며,
            서비스 콘텐츠를 근거로 한 학습 결과에 대해 서비스는 법적 책임을 지지 않습니다.
            오류를 발견한 경우 문의 채널로 제보해 주시면 검토 후 반영합니다.
          </p>
        </Section>

        <Section title="제5조 (이용자의 의무)">
          <ul className="list-disc pl-5 space-y-1">
            <li>이용자는 관련 법령과 본 약관을 준수해야 합니다.</li>
            <li>서비스의 콘텐츠를 무단으로 복제·배포·판매하거나 상업적으로 이용해서는 안 됩니다.</li>
            <li>서비스의 정상적인 운영을 방해하는 행위(자동화된 대량 접근, 시스템 공격 등)를 해서는 안 됩니다.</li>
          </ul>
        </Section>

        <Section title="제6조 (지식재산권)">
          <p>
            서비스가 제작한 해설, 화면 구성, 디자인, 소프트웨어 등에 대한 지식재산권은
            서비스에 귀속됩니다. 기출문제 원문에 대한 권리는 각 시험 시행 기관에 있으며,
            서비스는 학습 목적의 범위에서 이를 제공합니다.
          </p>
        </Section>

        <Section title="제7조 (광고의 게재)">
          <p>
            서비스는 운영을 위해 Google AdSense 등 제3자 광고를 게재할 수 있습니다.
            광고와 관련한 개인정보·쿠키 처리에 대한 자세한 내용은{' '}
            <a href="/privacy" className="text-orange-600 underline">개인정보처리방침</a>을
            참고하시기 바랍니다.
          </p>
        </Section>

        <Section title="제8조 (면책)">
          <p>
            서비스는 천재지변, 시스템 장애, 제3자의 귀책 등 통제할 수 없는 사유로 인한
            서비스 중단에 대해 책임을 지지 않습니다. 또한 서비스는 무료로 제공되는
            학습 자료의 활용 결과(시험 합격 여부 등)를 보장하지 않습니다.
          </p>
        </Section>

        <Section title="제9조 (문의)">
          <p>
            본 약관과 관련한 문의는{' '}
            <a href={`mailto:${SITE_CONTACT}`} className="text-orange-600 underline">{SITE_CONTACT}</a>
            로 연락해 주시기 바랍니다.
          </p>
        </Section>

        <div className="border-t border-gray-200 mt-10 pt-6 text-sm text-gray-500 flex flex-wrap gap-4">
          <a href="/about" className="hover:text-orange-600">서비스 소개</a>
          <a href="/privacy" className="hover:text-orange-600">개인정보처리방침</a>
          <a href="/terms" className="hover:text-orange-600">이용약관</a>
          <a href="/contact" className="hover:text-orange-600">문의</a>
        </div>
      </main>
    </div>
  );
}
